import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { invalidatePublicSettings } from '../../hooks/usePublicSettings';
import DashboardLayout from '../../components/DashboardLayout';
import { Skeleton } from '../../components/Skeleton';
import Icon from '../../components/Icon';

const PLANS = ['monthly', 'quarterly', 'biannual'];

export default function AdminSettings() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState({ monthly: '', quarterly: '', biannual: '', extraServerDiscountPct: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api.adminSettings()
      .then((r) => setPricing({
        monthly: String(r.pricing.monthly),
        quarterly: String(r.pricing.quarterly),
        biannual: String(r.pricing.biannual),
        extraServerDiscountPct: String(r.pricing.extraServerDiscountPct ?? 35),
      }))
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setMsg(null);
    setBusy(true);
    try {
      const payload = {
        pricing: {
          monthly: Number(pricing.monthly),
          quarterly: Number(pricing.quarterly),
          biannual: Number(pricing.biannual),
          extraServerDiscountPct: Number(pricing.extraServerDiscountPct),
        },
      };
      const r = await api.updateAdminSettings(payload);
      setPricing({
        monthly: String(r.pricing.monthly),
        quarterly: String(r.pricing.quarterly),
        biannual: String(r.pricing.biannual),
        extraServerDiscountPct: String(r.pricing.extraServerDiscountPct ?? 35),
      });
      invalidatePublicSettings(); // landing + checkout yeni fiyatı çeksin
      setMsg({ tone: 'ok', text: t('adminSettings.saved') });
    } catch (err) {
      setMsg({ tone: 'crit', text: err.message || t('admin.err_generic') });
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(null), 5000);
    }
  };

  const monthlyEquiv = {
    monthly: Number(pricing.monthly) || 0,
    quarterly: (Number(pricing.quarterly) || 0) / 3,
    biannual: (Number(pricing.biannual) || 0) / 6,
  };

  return (
    <DashboardLayout title={t('adminSettings.title')} crumb={t('admin.title')} heading={false}>
      <h1 className="page-title">{t('adminSettings.title')}</h1>
      <p className="page-sub">{t('adminSettings.subtitle')}</p>

      {msg && (
        <div className="form-error" style={{
          background: msg.tone === 'ok' ? 'var(--ok-soft)' : 'var(--crit-soft)',
          borderColor: 'transparent', color: msg.tone === 'ok' ? 'var(--ok)' : 'var(--crit)',
        }}>
          <Icon name={msg.tone === 'ok' ? 'check' : 'alert-triangle'} size={14} /> {msg.text}
        </div>
      )}

      {loading ? (
        <div className="card"><Skeleton className="sk-block" /></div>
      ) : (
        <form className="card" onSubmit={save}>
          <h3 style={{ fontSize: '1rem', marginBottom: 6 }}>{t('adminSettings.pricing_title')}</h3>
          <p style={{ color: 'var(--text-3)', fontSize: 12.5, marginBottom: 18 }}>{t('adminSettings.pricing_desc')}</p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
            {PLANS.map((p) => (
              <div className="field" key={p} style={{ marginBottom: 0 }}>
                <label>{t(`checkout.plan_${p}`)} (₺)</label>
                <input
                  type="number" min="1" step="0.01" required
                  value={pricing[p]}
                  onChange={(e) => setPricing((s) => ({ ...s, [p]: e.target.value }))}
                />
                <div className="hint">≈ ₺{Math.round(monthlyEquiv[p])}/{t('checkout.per_month').replace('/', '')}</div>
              </div>
            ))}
          </div>

          <div className="field" style={{ marginTop: 20, maxWidth: 260 }}>
            <label>{t('adminSettings.extra_server_discount')}</label>
            <input type="number" min="0" max="90" step="1" required
              value={pricing.extraServerDiscountPct}
              onChange={(e) => setPricing((s) => ({ ...s, extraServerDiscountPct: e.target.value }))}
            />
            <div className="hint">{t('adminSettings.extra_server_hint')}</div>
          </div>

          <button className="btn" style={{ marginTop: 20 }} disabled={busy}>
            {busy ? t('common.loading') + '…' : t('adminSettings.save')}
          </button>
          <p className="hint" style={{ marginTop: 10 }}>{t('adminSettings.live_note')}</p>
        </form>
      )}
    </DashboardLayout>
  );
}
